import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface NavbarProps {
  variant?: 'transparent' | 'solid';
}

export default function Navbar({ variant = 'transparent' }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isSolid = variant === 'solid' || isScrolled;

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}>
        <div className="px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
          {/* Left - Menu Button */}
          <button 
            onClick={() => setIsMenuOpen(true)}
            className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
              isSolid ? 'hover:bg-black/5' : 'hover:bg-white/10'
            }`}
          >
            <i className={`ri-menu-line text-xl ${isSolid ? 'text-black' : 'text-white'}`}></i>
          </button>

          {/* Center - Logo */}
          <Link to="/" className="absolute left-1/2 -translate-x-1/2 cursor-pointer">
            <span className={`text-xl sm:text-2xl font-bold tracking-tight ${
              isSolid ? 'text-black' : 'text-white'
            }`}>
              Jawsthe
            </span>
          </Link>

          {/* Right - Search & Cart */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
              isSolid ? 'hover:bg-black/5' : 'hover:bg-white/10'
            }`}>
              <i className={`ri-search-line text-xl ${isSolid ? 'text-black' : 'text-white'}`}></i>
            </button>
            <button className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors relative cursor-pointer ${
              isSolid ? 'hover:bg-black/5' : 'hover:bg-white/10'
            }`}>
              <i className={`ri-shopping-cart-line text-xl ${isSolid ? 'text-black' : 'text-white'}`}></i>
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-black text-white text-[10px] rounded-full flex items-center justify-center font-medium">
                0
              </span>
            </button>
          </div>
        </div>
      </nav>

      {/* Slide-in Menu */}
      <div className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}>
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50"
          onClick={() => setIsMenuOpen(false)}
        ></div>

        {/* Menu Panel */}
        <div className={`absolute top-0 left-0 bottom-0 w-full sm:w-80 bg-white shadow-2xl transition-transform duration-300 ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-black/10">
              <span className="text-xl font-bold">Menu</span>
              <button 
                onClick={() => setIsMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Navigation Links */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <nav className="space-y-1">
                <Link 
                  to="/" 
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-3 text-base font-medium text-black hover:bg-black/5 rounded-lg transition-colors cursor-pointer"
                >
                  Home
                </Link>
                <a 
                  href="#products" 
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-3 text-base font-medium text-black hover:bg-black/5 rounded-lg transition-colors cursor-pointer"
                >
                  Shop
                </a>
                <a 
                  href="#how-it-works" 
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-3 text-base font-medium text-black hover:bg-black/5 rounded-lg transition-colors cursor-pointer"
                >
                  How It Works
                </a>
                <a 
                  href="#reviews" 
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-3 text-base font-medium text-black hover:bg-black/5 rounded-lg transition-colors cursor-pointer"
                >
                  Reviews
                </a>
              </nav>

              {/* Divider */}
              <div className="my-6 border-t border-black/10"></div>

              {/* Secondary Links */}
              <div className="space-y-1">
                <a 
                  href="#" 
                  className="block px-4 py-3 text-sm text-black/60 hover:bg-black/5 rounded-lg transition-colors cursor-pointer"
                >
                  My Account
                </a>
                <a 
                  href="#" 
                  className="block px-4 py-3 text-sm text-black/60 hover:bg-black/5 rounded-lg transition-colors cursor-pointer"
                >
                  FAQ
                </a>
                <a 
                  href="#" 
                  className="block px-4 py-3 text-sm text-black/60 hover:bg-black/5 rounded-lg transition-colors cursor-pointer"
                >
                  Contact
                </a>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-6 border-t border-black/10">
              <div className="flex items-center justify-center gap-4 mb-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors cursor-pointer">
                  <i className="ri-facebook-line text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-xl"></i>
                </a>
              </div>
              <p className="text-xs text-center text-black/40">
                © 2024 Jawsthe. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
